import React from "react";
import { NodeProps, useReactFlow } from "reactflow";
import axios from "axios";
import styled from 'styled-components';
import { operationType, selectDataType } from "../../../../../services/types";
import { updateNodeValue } from "../../../../../services/method";
import { webURL } from "../../../../../services/API";
import { Select } from "../../../../form/select";
import { TextInput } from "../../../../form/textInput";
import { Textarea } from "../../../../form/textarea";
import { SettingsBlock, settingsDataType } from "../../../../settingsBlock";
import { IoCheckmarkCircleOutline, IoWarningOutline } from "react-icons/io5";
import { ExpandedBlock } from "../../../expanded";
import { Styled } from "./style";

type DataTableProps = NodeProps<operationType> & {
   setChildMinimize?: (fn: (id: string) => void) => void;
   setChildSettings?: (fn: (id: string) => void) => void;
};

const requests: { id: number, name: string, refId: string }[] = [
   { id: 1, name: 'Get rows (list/filter)', refId: 'get_rows' },
   { id: 2, name: 'Get one row', refId: 'get_row' },
   { id: 3, name: 'Append row', refId: 'append_row' },
   { id: 4, name: 'Update row', refId: 'update_row' },
   { id: 5, name: 'Update or append row', refId: 'upsert_row' },
   { id: 6, name: 'Delete row', refId: 'delete_row' },
];

export function DataTable({ id, data, setChildMinimize, setChildSettings }: DataTableProps) {
   const { setNodes } = useReactFlow();
   const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
   const [isExpanded, setIsExpanded] = React.useState(false);
   const [tables, setTables] = React.useState<selectDataType[]>([]);
   const [isLoadingTables, setIsLoadingTables] = React.useState(false);
   const [isCreating, setIsCreating] = React.useState(false);
   const [newTableName, setNewTableName] = React.useState('');
   const [newTableColumns, setNewTableColumns] = React.useState('');

   // --
   React.useEffect(() => {
      if (setChildMinimize) setChildMinimize(() => { setIsExpanded(prev => !prev) });
      if (setChildSettings) setChildSettings(() => { setSettings(prev => ({ ...prev, isToggle: !prev.isToggle })) });
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [setChildMinimize, setChildSettings]);

   // --
   React.useEffect(() => {
      if (!data.value) updateNodeValue(id, setNodes, { request: requests[0].refId });
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   const fetchTables = React.useCallback(async () => {
      setIsLoadingTables(true);
      try {
         const response = (await axios.get(`${webURL}/dataTables`)).data;
         setTables((response || []).map((t: any) => ({ id: t._id, refId: t._id, name: t.name, isActive: t._id === data.value?.table_id })));
      } catch {
         setTables([]);
      }
      setIsLoadingTables(false);
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [data.value?.table_id]);

   React.useEffect(() => { fetchTables(); }, [fetchTables]);

   // ---
   const handleSelectTable = (tableId: any) => {
      setTables(list => list.map(t => ({ ...t, isActive: t.id === tableId })));
      updateNodeValue(id, setNodes, { table_id: tableId });
   }

   // ---
   const handleSelectRequest = (reqId: number) => {
      const refId = requests.find(r => r.id === reqId)?.refId;
      updateNodeValue(id, setNodes, { request: refId, search_key: '', search_value: '', values: '' });
   }

   // ---
   const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>, keyRef?: string) => {
      if (keyRef) updateNodeValue(id, setNodes, { [keyRef]: e.target.value });
   }

   // ---
   const handleCreateTable = async () => {
      if (!newTableName.trim()) return;
      try {
         const columns = newTableColumns.split(',').map(c => c.trim()).filter(Boolean).map(key => ({ key, type: 'text' }));
         const response = (await axios.post(`${webURL}/dataTables`, { name: newTableName.trim(), columns })).data;
         setNewTableName('');
         setNewTableColumns('');
         setIsCreating(false);
         await fetchTables();
         if (response?.table?._id) updateNodeValue(id, setNodes, { table_id: response.table._id });
      } catch {}
   }

   // ---
   const request = data.value?.request;
   const needsSearch = ['get_row', 'update_row', 'upsert_row', 'delete_row'].includes(request);
   const needsValues = ['append_row', 'update_row', 'upsert_row'].includes(request);
   const isWarning = !data.value?.table_id;

   return (<>
      <SettingsBlock nodeId={ id } data={ settings } setData={ setSettings } />
      <Main>
         { isWarning ? <>
            <div id="warning"></div>
            <span id="warningIcon"><IoWarningOutline size={21} /></span>
         </> : data.output ? <>
            <div id="ran"></div>
            <span id="ranIcon"><IoCheckmarkCircleOutline size={21} /></span>
         </> : null }
         { isExpanded ?
         <ExpandedBlock
            data={{ id, ...data }}
            handleClose={ () => setIsExpanded(false) }
            isWarning={ isWarning }
            style={{ minWidth: '380px' }}
         ><Styled>
            <Select
               title={ isLoadingTables ? 'Loading Tables...' : 'Table' }
               data={ tables }
               handleSelectItem={ handleSelectTable }
               isSmall={ true }
               emptyMsg="No tables yet — create one below"
            />
            { !isCreating ? (
               <button type="button" className="linkBtn" onClick={ () => setIsCreating(true) }>+ New Table</button>
            ) : (
               <div className="newTable">
                  <TextInput keyRef="__newTableName" title="Table Name" placeholder="Properties" value={ newTableName } handleChange={ (e: any) => setNewTableName(e.target.value) } />
                  <TextInput keyRef="__newTableColumns" title="Columns" placeholder="propertyId, location, budget" tooltip="Comma-separated column names for this table." value={ newTableColumns } handleChange={ (e: any) => setNewTableColumns(e.target.value) } />
                  <div className="newTableActions">
                     <button type="button" className="btn1 withBorder" onClick={ handleCreateTable }>Create</button>
                     <button type="button" className="btn1 withBorder delete" onClick={ () => setIsCreating(false) }>Cancel</button>
                  </div>
               </div>
            )}

            <Select title="Request" data={ requests.map(r => ({ ...r, isActive: r.refId === request })) } handleSelectItem={ handleSelectRequest } isSmall={ true } />

            { needsSearch ? <>
               <TextInput
                  keyRef="search_key" title="Search Column" placeholder="e.g. propertyId"
                  tooltip="The column name to match against — e.g. propertyId or email."
                  value={ data.value?.search_key || '' } handleChange={ handleChangeInput }
               />
               <TextInput
                  keyRef="search_value" title="Search Value" placeholder="e.g. {{ $block... }} or a literal value"
                  tooltip="The value that column must equal to find the row."
                  value={ data.value?.search_value || '' } handleChange={ handleChangeInput }
               />
            </> : null }

            { needsValues ? <Textarea
               keyRef="values"
               title="Values"
               value={ data.value?.values || '' }
               placeholder={ '{"propertyId": "P1", "location": "Cairo", "status": "New"}' }
               tooltip="A JSON object of column:value pairs to write. 'key=value, key2=value2' also works."
               handleChange={ (e: any, keyRef?: string) => updateNodeValue(id, setNodes, { [keyRef as string]: e.target.value }) }
            /> : null }
         </Styled></ExpandedBlock> : null }
      </Main>
   </>);
}

// ---
const Main = styled.div`
   min-width: 150px;
   padding: 0;
`;
